/**
 * The proration PREVIEW the confirm screen shows (APP-ENTITLEMENT-001, Q-AE-7: add = prorated now, remove =
 * prorated credit now). Stripe is the authority on the charge; this is the number a person reads before they
 * confirm, computed the way Stripe computes it — the unused fraction of the current period, by elapsed time.
 *
 * @module @puffin-core/app-catalog/rules/proration
 */

'use strict';

function instant(value, name) {
  const ms = value instanceof Date ? value.getTime() : Date.parse(value);
  if (!Number.isFinite(ms)) {
    throw new TypeError(`${name} must be an ISO instant or a Date, got ${JSON.stringify(value)}`);
  }
  return ms;
}

function assertCents(value, name) {
  if (!Number.isInteger(value) || value < 0) {
    throw new TypeError(
      `${name} must be a non-negative integer of cents, got ${JSON.stringify(value)}`,
    );
  }
}

/**
 * The fraction of the period [periodStart, periodEnd) still ahead at `now`, in [0, 1].
 *
 * @param {{periodStart: string|Date, periodEnd: string|Date, now: string|Date}} params
 * @returns {number}
 */
function remainingFraction({ periodStart, periodEnd, now }) {
  const start = instant(periodStart, 'periodStart');
  const end = instant(periodEnd, 'periodEnd');
  if (end <= start) throw new TypeError('periodEnd must be after periodStart');
  const at = instant(now, 'now');
  if (at <= start) return 1;
  if (at >= end) return 0;
  return (end - at) / (end - start);
}

/**
 * The prorated amount for a quantity change made at `now`: positive for an add (charged today), negative for a
 * removal (credited today). Rounded to the cent toward zero, so a preview never overstates a charge or a credit.
 *
 * @param {Object} params
 * @param {number} params.unitAmountCents - the unit's price for the whole period.
 * @param {number} params.quantityDelta - units added (positive) or removed (negative); an integer.
 * @param {string|Date} params.periodStart
 * @param {string|Date} params.periodEnd
 * @param {string|Date} params.now
 * @returns {{amountCents: number, remainingFraction: number}}
 */
function prorationPreview({ unitAmountCents, quantityDelta, periodStart, periodEnd, now }) {
  assertCents(unitAmountCents, 'unitAmountCents');
  if (!Number.isInteger(quantityDelta)) {
    throw new TypeError(`quantityDelta must be an integer, got ${JSON.stringify(quantityDelta)}`);
  }
  const fraction = remainingFraction({ periodStart, periodEnd, now });
  const raw = unitAmountCents * quantityDelta * fraction;
  return { amountCents: Math.trunc(raw), remainingFraction: fraction };
}

module.exports = { remainingFraction, prorationPreview };
